import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useNavigate,
    useParams,
    useSearchParams,
    useLocation,
} from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../ui/button';
import LoadingRing from '../../ui-components/LoadingRing';
import { DocumentSchema } from '../../documents/types';
import { PolicyMetadata } from './PolicyMetadataBar';
import { PolicyEditorContent } from './PolicyEditorContent';
import { getPolicy } from '../../../services/api/policyService';
import { getDocumentSchema } from '../../../services/documentSchemaService';
import {
    DepartmentVariableTemplateSection,
    getDepartmentVariableTemplate,
    getDepartmentVariables,
    resolveVariables,
} from '../../../services/api/departmentVariablesService';
import type { Variable } from '../../../services/api/departmentVariablesService';
import type { VariableOption } from '../../documents/form/inputs/mentionSuggestion';
import { toLabel } from '../../department-variables/utils/variableUtils';
import { hydrateTemplate } from '../../department-variables/utils/hydrateTemplate';
import { VariableSection } from '../../department-variables/utils/variableTypes';
import { DocumentStatus } from '../../documents/types';
import { mergePersistedConditionalsIntoDocumentData } from '../conditionals/conditionalsUtils';

function buildVariableOptions(
    variables: Variable[],
    sections: VariableSection[]
): VariableOption[] {
    const defaultLookup = new Map<string, { label: string; section: string }>();
    for (const section of sections) {
        for (const v of section.variables) {
            defaultLookup.set(v.name, {
                label: v.label || toLabel(v.name),
                section: section.title,
            });
        }
    }

    const filled = variables.filter((v) =>
        v.type === 'fixed' ? !!v.fixedValue : (!!v.refId || !!v.fixedValue)
    );

    return filled.map((v) => {
        const defaults = defaultLookup.get(v.name);
        return {
            name: v.name,
            label: defaults?.label || v.label || toLabel(v.name),
            section: defaults?.section || 'Custom',
        };
    });
}

interface LoadedData {
    schema: DocumentSchema;
    initialPolicyId: string | undefined;
    initialDocData: { data: Record<string, any>; status: DocumentStatus } | undefined;
    variableMap: Record<string, string>;
    variableOptions: VariableOption[];
    initialMeta: PolicyMetadata;
}

export default function PolicyEditorPage() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const navState = location.state as {
        effectiveDate?: string;
        expiryDate?: string;
    } | null;
    const schemaKeyParam = searchParams.get('schema');
    const templateIdParam = searchParams.get('template');

    const [loaded, setLoaded] = useState<LoadedData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                // resolve the schema key
                let schemaKey: string;
                let schemaVersion: number | undefined;
                let initialPolicyId: string | undefined;
                let initialDocData: LoadedData['initialDocData'];
                let initialMeta: PolicyMetadata;

                if (id) {
                    const policyData = await getPolicy(id);
                    const doc = policyData.document_id;

                    if (!doc.schemaKey) {
                        setError('Policy has no document or schema reference');
                        return;
                    }

                    schemaKey = doc.schemaKey;
                    schemaVersion = doc.schemaVersion;
                    initialPolicyId = id;
                    initialDocData = {
                        data: mergePersistedConditionalsIntoDocumentData(
                            doc.data,
                            policyData.conditionals
                        ),
                        status: doc.status,
                    };
                    initialMeta = {
                        title: doc.title || '',
                        effectiveDate:
                            policyData.effectiveDate?.substring(0, 10) || '',
                        expiryDate:
                            policyData.expiryDate?.substring(0, 10) || '',
                        tags: policyData.tags ?? [],
                        coAuthors: policyData.coAuthors ?? [],
                        attachments: policyData.attachments ?? [],
                        requireApproval: policyData.requireApproval ?? false,
                    };
                } else if (templateIdParam) {
                    const templatePolicy = await getPolicy(templateIdParam);
                    const doc = templatePolicy.document_id;

                    if (!doc.schemaKey) {
                        setError('Template has no document or schema');
                        return;
                    }

                    schemaKey = doc.schemaKey;
                    schemaVersion = doc.schemaVersion;
                    initialDocData = {
                        data: mergePersistedConditionalsIntoDocumentData(
                            doc.data,
                            templatePolicy.conditionals
                        ),
                        status: 'draft',
                    };
                    initialMeta = {
                        title: '',
                        effectiveDate: navState?.effectiveDate || '',
                        expiryDate: navState?.expiryDate || '',
                        tags: [],
                        coAuthors: [],
                        attachments: [],
                        requireApproval: false,
                    };
                } else if (schemaKeyParam) {
                    schemaKey = schemaKeyParam;
                    initialMeta = {
                        title: '',
                        effectiveDate: navState?.effectiveDate || '',
                        expiryDate: navState?.expiryDate || '',
                        tags: [],
                        coAuthors: [],
                        attachments: [],
                        requireApproval: false,
                    };
                } else {
                    setError('No policy ID or schema specified');
                    return;
                }

                // load schema and dept variables
                const [schemaRecord, varMap, rawVars, varSections] =
                    await Promise.all([
                        getDocumentSchema(schemaKey, schemaVersion),
                        resolveVariables().catch(() => ({})),
                        getDepartmentVariables().catch((): Variable[] => []),
                        getDepartmentVariableTemplate().catch(
                            (): DepartmentVariableTemplateSection[] => []
                        ),
                    ]);

                // action btn titles translated
                const actionsSection = schemaRecord.schema?.sections?.find(
                    (s: any) => s?.sectionType === 'actions'
                );
                if (actionsSection?.fields) {
                    for (const field of actionsSection.fields) {
                        if (!field?.id) continue;
                        field.title = t(
                            `departmentPolicies.actions.${field.id}`,
                            { defaultValue: field.title }
                        );
                    }
                }

                setLoaded({
                    schema: schemaRecord.schema,
                    initialPolicyId,
                    initialDocData,
                    variableMap: varMap,
                    variableOptions: buildVariableOptions(rawVars, hydrateTemplate(varSections, t)),
                    initialMeta,
                });
            } catch (err) {
                console.error('Failed to load:', err);
                setError('Failed to load policy');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, schemaKeyParam, templateIdParam]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (error || !loaded) {
        return (
            <div className="container mx-auto max-w-3xl px-4 py-8">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/department-policies')}
                    className="mb-4"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    {t('departmentPolicies.title')}
                </Button>
                <p className="text-sm text-muted-foreground">
                    {error || t('departmentPolicies.readPage.policyNotFound')}
                </p>
            </div>
        );
    }

    return (
        <PolicyEditorContent
            {...loaded}
            templateIdParam={templateIdParam}
            schemaKeyParam={schemaKeyParam}
        />
    );
}
